import React from 'react';
import PropTypes from "prop-types";
import { useTable, useSortBy } from 'react-table';
import Box from '../Box';
import Icon from '../Icon';
import { makeStyles } from '../../utils/styles';
import { StyledTableContainer, StyledTableWrapper, StyledTable, StyledThead, StyledTbody, StyledTfoot, StyledTr, StyledTh, StyledTd } from './styled';
import Typography from '../Typography';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(css => ({
    boxHeader: css`
        display: inline-flex;
        align-items: center;
    `,
    boxIcon: css`
        display: inline-flex;
        align-items: center;
        margin-left: 15px;
    `
}));

const Table = ({ columns, data, guttersCell, hideFooter }) => {
    const {t} = useTranslation()
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        footerGroups,
        visibleColumns,
        rows,
        prepareRow,
    } = useTable(
        {
            columns,
            data
        },
        useSortBy
    );
    const styles = useStyles();

    return (
        <StyledTableContainer>
            <StyledTableWrapper>
                <StyledTable
                    {...getTableProps()}
                    guttersCell={guttersCell}
                >
                    <StyledThead>
                        {headerGroups.map(headerGroup => (
                            <StyledTr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <StyledTh {...column.getHeaderProps([{ style: column.style }, column.getSortByToggleProps()])}>
                                        <Box
                                            as="span"
                                            customStyle={styles.boxHeader}
                                        >
                                            {column.render('Header')}
                                            {
                                                column.isSorted &&
                                                <Box
                                                    as="span"
                                                    customStyle={styles.boxIcon}
                                                >
                                                    {column.isSortedDesc ? <Icon name="arrow-down" /> : <Icon name="arrow-up" />}
                                                </Box>
                                            }
                                        </Box>
                                    </StyledTh>
                                ))}
                            </StyledTr>
                        ))}
                    </StyledThead>
                    <StyledTbody {...getTableBodyProps()}>
                        {rows.map((row, i) => {
                            prepareRow(row)
                            return (
                                <StyledTr {...row.getRowProps()}>
                                    {row.cells.map(cell => {
                                        return <StyledTd {...cell.getCellProps([{ style: cell.column.style }])}>{cell.render('Cell')}</StyledTd>
                                    })}
                                </StyledTr>
                            )
                        })}
                        {
                            rows.length === 0 &&
                            <StyledTr>
                                <StyledTd colSpan={visibleColumns.length}>
                                    <Typography align="center">
                                        {t("Brak danych")}
                                    </Typography>
                                </StyledTd>
                            </StyledTr>
                        }
                    </StyledTbody>
                    {
                        (!hideFooter && rows.length !== 0) && (
                            <StyledTfoot>
                                {footerGroups.map(group => (
                                    <StyledTr {...group.getFooterGroupProps()}>
                                        {group.headers.map(column => (
                                            <StyledTd {...column.getFooterProps()}>{column.render('Footer')}</StyledTd>
                                        ))}
                                    </StyledTr>
                                ))}
                            </StyledTfoot>
                        )
                    }
                </StyledTable>
            </StyledTableWrapper>
        </StyledTableContainer>
    );
};

Table.propTypes = {
    columns: PropTypes.array,
    data: PropTypes.array,
    guttersCell: PropTypes.oneOf(['small', 'large']),
    hideFooter: PropTypes.bool
};

Table.defaultProps = {
    columns: [],
    data: [],
    guttersCell: 'large',
    hideFooter: false
};

export default React.memo(Table);